/* eslint-disable no-nested-ternary */
import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import Dropzone from 'react-dropzone';
import Dropdown from 'react-bootstrap/Dropdown';

import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

import useFeed from '../hooks/useFeed';
import { setSubActiveReduxModal } from '../../ducks/feedActions';

const ActionButton = styled('div')`
  display: flex;
  height: ${(props) => (!props.small ? '56px' : '40px')};
  width: ${(props) => (!props.small ? '56px' : '40px')};
  background-color: ${(props) => (props.uploadedMedia ? '#0dcc8a' : '')};
  border-radius: 100%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${TransitionGlobal};
  border: ${(props) => (!props.uploadedMedia ? '1px solid #9e9e9e' : '1px solid #0dcc8a')};
  svg {
    path {
      fill: #ffffff;
    }
  }
`;

const DropDownMenuWrapper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 20%;
  color: #333333;
  z-index: 2;

  &.giphy-dropdown {
    position: absolute;
    top: 0px;
    right: 20px;
    height: 100%;
    z-index: 100;
    cursor: pointer;
  }

  &.emoji-dropdown {
    position: absolute;
    top: 0px;
    right: 55px;
    height: 100%;
    z-index: 100;
    cursor: pointer;
  }

  .dropdown-container {
    margin-bottom: 10px;
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);
    border: 0px;

    &.giphy {
      padding: 20px;
      z-index: 100;
    }

    &.emoji {
      padding: 0px;
      z-index: 100;
    }
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    padding: 15px 20px;
    transition: none;
    span {
      color: #444444;
    }
    :hover {
      background: #006a65;
      color: #ffffff;
      span {
        color: #ffffff;
      }
      svg {
        fill: #ffffff;
      }
    }
  }
  .dropdown-menu {
    padding: 10px 0px;
  }
  .dropdown-divider {
    margin: 0px;
  }

  .add-gif-button {
    height: 32px;
    width: 32px;
    color: #444444;
    background-color: transparent;
    font-weight: 800;
    letter-spacing: -0.5px;

    &:hover {
      opacity: 0.7;
      background-color: transparent;
    }
  }

  &.notification-dropdown {
    position: absolute;
    right: 15px;
  }
`;

const DropZoneWrapper = styled('div')`
  display: flex;
  justify-content: center;
  cursor: pointer;
  flex-direction: column;
  .upload-image-text {
    margin-left: 10px;
    margin-top: 10px;
    margin-bottom: 15px;
    font-size: 14px;
    color: #ff6600;
  }

  .update-button {
    width: 180px;
    margin-top: 15px;
    background-color: ${(props) => props.theme.colors.green};
    color: #ffffff;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const EmptyDropZoneWrapper = styled('div')`
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  margin: 0 15px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  min-height: 30px;
  color: #495057;
  border: 0px solid #ced4da;
  border-radius: 50%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  @media screen and (max-width: 940px) {
    margin: 0 8px;
  }
`;

const MediaDropdownContainer = styled('div')`
  display: flex;
  justify-content: center;
  height: 38px;
  width: 38px;
  margin: 0 15px;
  @media screen and (max-width: 940px) {
    margin: 0 8px;
  }
`;

const SpinnerWrapper = styled('div')`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const AddMediaToggle = React.forwardRef(
  ({ children, onClick, uploadedMedia }, ref) => (
    <ActionButton
      uploadedMedia={uploadedMedia}
      href=''
      icon='plus'
      small
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      ref={ref}
      aria-label='Attach media'
    >
      {children}
      <IcomoonReact
        iconSet={iconSet}
        size={20}
        icon='attachment'
        color={uploadedMedia ? '#ffffff' : '#9e9e9e'}
      />
    </ActionButton>
  ),
);

AddMediaToggle.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func.isRequired,
  uploadedMedia: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

AddMediaToggle.defaultProps = {
  children: null,
  uploadedMedia: '',
};

const AttachmentButton = () => {
  const { handleUploadMedia } = useFeed();
  const dispatch = useDispatch();
  const uploadedMedia = useSelector((state) => state.feed.data.uploadMedia);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isMediaLoading = useSelector((state) => state.feed.ui.isMediaLoading);

  const handleDroppedFile = useCallback((acceptedFile) => {
    const type = acceptedFile[0].type.split('/').shift();
    handleUploadMedia({
      accountCode: currentUser.accountCode,
      acceptedFile,
      type,
    });
  }, [handleUploadMedia, currentUser.accountCode]);

  const dropdownIconTextStyle = {
    fontSize: 14,
    marginLeft: 10,
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: 0,
  };

  const fileAccepterSwitch = useMemo(() => (
    <Dropzone onDrop={(acceptedFile) => handleDroppedFile(acceptedFile)}>
      {({ getRootProps, getInputProps }) => (
        <DropZoneWrapper {...getRootProps()}>
          <input {...getInputProps()} />
          <Dropdown.Item isClicked={uploadedMedia} aria-label='Add saved media'>
            <>
              <IcomoonReact iconSet={iconSet} size={20} icon='attachment' />
              <Text styledTextProps={dropdownIconTextStyle}>
                Add Saved Media
              </Text>
            </>
          </Dropdown.Item>
        </DropZoneWrapper>
      )}
    </Dropzone>
  ), [handleDroppedFile, uploadedMedia, dropdownIconTextStyle]);

  const addMediaDropdown = useMemo(() => (
    <DropDownMenuWrapper>
      <Dropdown bsPrefix='trick-fix'>
        <Dropdown.Toggle as={AddMediaToggle} uploadedMedia={uploadedMedia} />
        <Dropdown.Menu className='dropdown-container emoji'>
          <Dropdown.Item
            className='dropdown-item'
            onClick={() => dispatch(setSubActiveReduxModal('record-image-modal'))}
            aria-label='Add live photo'
          >
            <IcomoonReact iconSet={iconSet} size={20} icon='camera' />
            <Text styledTextProps={dropdownIconTextStyle}>Add Live Photo</Text>
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => dispatch(setSubActiveReduxModal('record-video-modal'))}
            aria-label='Add live video'
          >
            <>
              <IcomoonReact iconSet={iconSet} size={20} icon='video' />
              <Text styledTextProps={dropdownIconTextStyle}>
                Add Live Video
              </Text>
            </>
          </Dropdown.Item>
          {fileAccepterSwitch}
        </Dropdown.Menu>
      </Dropdown>
    </DropDownMenuWrapper>
  ), [uploadedMedia, dispatch, dropdownIconTextStyle, fileAccepterSwitch]);

  const mediaDropdownContent = useMemo(() => {
    if (isMediaLoading) {
      return (
        <EmptyDropZoneWrapper>
          <SpinnerWrapper>
            <Spinner saving darkTheme large={false} />
          </SpinnerWrapper>
        </EmptyDropZoneWrapper>
      );
    }
    return addMediaDropdown;
  }, [isMediaLoading, addMediaDropdown]);

  return (
    <MediaDropdownContainer>{mediaDropdownContent}</MediaDropdownContainer>
  );
};

export default AttachmentButton;
